/**
 * Contrast, computed rather than eyeballed.
 *
 * The design rule this project has always carried is that text over photography
 * is checked by calculation against worst-case light pixels. It was carried as
 * a comment, and a recompute showed the homepage hero did not actually meet it:
 * against a blown-out pixel the eyebrow read 2.4:1 where it needs 4.5. So the
 * numbers live here now, where a test can hold them.
 *
 * Everything is in WCAG's relative luminance, and every alpha composite is done
 * in linear light, which is what the contrast formula expects.
 */

/** sRGB channel to linear light. */
const toLinear = (channel: number) => {
  const v = channel / 255;
  return v <= 0.04045 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
};

/** WCAG relative luminance of an sRGB colour. */
export const relativeLuminance = (r: number, g: number, b: number) =>
  0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b);

/** #0c1d33, the ground every scrim on this site is made of. */
export const MIDNIGHT_LUMINANCE = relativeLuminance(12, 29, 51);

/** One translucent layer over another. */
export const composite = (base: number, layer: number, alpha: number) => base * (1 - alpha) + layer * alpha;

export const contrastRatio = (a: number, b: number) =>
  (Math.max(a, b) + 0.05) / (Math.min(a, b) + 0.05);

/** 3.0 for large text, 4.5 for everything else. WCAG 2.2 AA. */
export const requiredRatio = (fontSizePx: number, fontWeight: number) =>
  fontSizePx >= 24 || (fontSizePx >= 18.66 && fontWeight >= 700) ? 3.0 : 4.5;

/**
 * The homepage hero's scrim, as the component paints it.
 *
 * The floor on the even tint is what the admin panel's overlay control is
 * clamped to: a super admin can deepen it and cannot lighten it past the value
 * the calculation was done against, because readability of white type on a
 * photograph is not a preference.
 */
export const HERO_BASE_TINT_FLOOR = 0.3;

/**
 * The scrim under the copy, anchored to the bottom of the section in pixels.
 *
 * Pixels, not percentages. The copy block is bottom-aligned and its height
 * changes with the display type, so on a tall viewport a percentage ramp let
 * the eyebrow drift up into the lighter part of it and lose two points of
 * contrast. `[distance from the bottom in px, alpha]`.
 */
export const HERO_SCRIM_STOPS: readonly (readonly [number, number])[] = [
  [0, 0.8],
  [420, 0.79],
  [720, 0.78],
  [860, 0.44],
  [980, 0.12],
  [1060, 0],
];

/** The scrim's alpha at a given distance above the bottom of the section. */
export function heroScrimAt(distanceFromBottomPx: number) {
  for (let i = 1; i < HERO_SCRIM_STOPS.length; i += 1) {
    const [p0, a0] = HERO_SCRIM_STOPS[i - 1];
    const [p1, a1] = HERO_SCRIM_STOPS[i];
    if (distanceFromBottomPx <= p1) {
      return a0 + (a1 - a0) * ((distanceFromBottomPx - p0) / (p1 - p0));
    }
  }
  return 0;
}

/**
 * What one line of hero copy actually reads at, against the brightest pixel
 * that can sit behind it.
 */
export function heroContrast({
  worstPixelLuminance,
  distanceFromBottomPx,
  textAlpha,
  baseTint = HERO_BASE_TINT_FLOOR,
  textLuminance = 1,
}: {
  worstPixelLuminance: number;
  distanceFromBottomPx: number;
  textAlpha: number;
  baseTint?: number;
  textLuminance?: number;
}) {
  const tinted = composite(worstPixelLuminance, MIDNIGHT_LUMINANCE, baseTint);
  const background = composite(tinted, MIDNIGHT_LUMINANCE, heroScrimAt(distanceFromBottomPx));
  const text = composite(background, textLuminance, textAlpha);
  return contrastRatio(text, background);
}

/** The opacity each line of hero copy is set in. Mirrored in Hero.tsx. */
export const HERO_TEXT_ALPHA = {
  eyebrow: 0.9,
  heading: 1,
  lead: 0.9,
  credibility: 0.85,
} as const;

/**
 * An interior page's photo banner.
 *
 * Unlike the hero, the banner's copy fills most of its height: breadcrumb,
 * label, heading, supporting line, description and buttons. A bottom-anchored
 * ramp would leave the breadcrumb on a light part of the photograph, so the scrim
 * is even across the whole height wherever copy can sit. On a wide screen the
 * copy is held to the left of the frame and the scrim eases off to the right, so
 * the photograph still reads; on a narrow one the copy spans the frame and so
 * does the scrim.
 *
 * 0.86 is the lightest even scrim at which the palest line (white at 90%) still
 * clears 4.5:1 over a pure white pixel, composited in linear light. An editor's
 * photograph cannot be checked in advance, so the calculation assumes the worst.
 */
export const BANNER_SCRIM = {
  /** Behind the copy, at every width. */
  copy: 0.86,
  /** Where the scrim eases off, beyond the copy on a wide screen. */
  edge: 0.5,
  /** The far edge on a wide screen, where no copy sits. */
  far: 0.3,
  /** How far across the frame, on a wide screen, the copy may reach. */
  copyWidthPercent: 56,
} as const;

export const BANNER_TEXT_ALPHA = {
  breadcrumb: 0.9,
  eyebrow: 0.9,
  heading: 1,
  lead: 0.9,
  description: 0.9,
} as const;

/** What one line of banner copy reads at, against the brightest pixel behind it. */
export function bannerContrast({
  worstPixelLuminance = 1,
  textAlpha,
  scrim = BANNER_SCRIM.copy,
}: {
  worstPixelLuminance?: number;
  textAlpha: number;
  scrim?: number;
}) {
  const background = composite(worstPixelLuminance, MIDNIGHT_LUMINANCE, scrim);
  const text = composite(background, 1, textAlpha);
  return contrastRatio(text, background);
}
